import { DownOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical, useApi } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { MenuProps } from 'antd'
import { Checkbox, Dropdown, Input, Popover, message, notification } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import type { InvoiceGroup } from '../../../../../api/useFreight/types'
import { useInvoices } from '../../../../../api/useFreight/useInvoices'
import { getAPIURL } from '../../../../../utils/api'
import { useSecurityContext } from '../../../../Navigation/SecurityContext'
import { timeDisplay } from '../../../shared/util/timeDisplay'
import {
  AddCustomerNoteButton,
  ApproveCreditButton,
  CreditRebillButton,
  InvoiceDocumentsSection,
  type InvoiceStatus,
  ManageHoldButton,
  RejectCreditButton,
  ResendButton,
  SendButton,
  invoiceStatusConfig,
} from './'
import { InvoiceCommentsContainer } from './InvoiceCommentsContainer'

export function InvoiceDetailPane({
  invoiceGroup,
  triggerRebillMode,
  isTriggeringCR,
}: {
  invoiceGroup: InvoiceGroup
  triggerRebillMode: () => void
  isTriggeringCR: boolean
}) {
  const { scopes } = useSecurityContext()
  const [isNotePopoverVisible, setIsNotePopoverVisible] = useState(false)

  const canManageHold =
    invoiceGroup.status === 'open' ||
    invoiceGroup.status === 'ready' ||
    invoiceGroup.status === 'blocked' ||
    invoiceGroup.status === 'hold' ||
    invoiceGroup.status === 'needs_attention'

  const canSend = invoiceGroup.status === 'open' || invoiceGroup.status === 'ready' || invoiceGroup.status === 'hold'

  const showDocuments = invoiceGroup.status === 'ready' || invoiceGroup.status === 'sent'
  const [isEditPopoverVisible, setIsEditPopoverVisible] = useState(false)
  const [customerNote, setCustomerNote] = useState('')
  const [savedNote, setSavedNote] = useState(invoiceGroup.invoices?.[0]?.note || '')
  const queryClient = useQueryClient()

  const { useAddNoteMutation, useRemoveNoteMutation } = useInvoices()
  const addNoteMutation = useAddNoteMutation()
  const removeNoteMutation = useRemoveNoteMutation()

  const handleSaveNote = async () => {
    if (customerNote.trim() && invoiceGroup.invoices?.[0]?.invoice_number) {
      try {
        await addNoteMutation.mutateAsync({
          invoice_group_id: invoiceGroup.id,
          invoice_number: invoiceGroup.invoices[0].invoice_number,
          msg: customerNote.trim(),
        })

        setSavedNote(customerNote.trim())
        setCustomerNote('')
        setIsNotePopoverVisible(false)
        setIsEditPopoverVisible(false)

        queryClient.invalidateQueries({
          queryKey: ['invoice', invoiceGroup.id],
        })

        message.success('Customer note added successfully')
      } catch (error) {
        console.error('Error saving note:', error)
        message.error('Failed to save customer note')
      }
    }
  }

  const handleEditNote = () => {
    setCustomerNote(savedNote)
    setIsEditPopoverVisible(true)
  }

  const handleRemoveNote = async () => {
    if (invoiceGroup.invoices?.[0]?.invoice_number) {
      try {
        await removeNoteMutation.mutateAsync({
          invoice_group_id: invoiceGroup.id,
          invoice_number: invoiceGroup.invoices[0].invoice_number,
        })

        setSavedNote('')
        setCustomerNote('')
        setIsEditPopoverVisible(false)

        queryClient.invalidateQueries({
          queryKey: ['invoice', invoiceGroup.id],
        })

        message.success('Customer note removed successfully')
      } catch (error) {
        console.error('Error removing note:', error)
        message.error('Failed to remove customer note')
      }
    }
  }

  // Update effect to also set the saved note when invoice group changes
  React.useEffect(() => {
    if (invoiceGroup) {
      if (invoiceGroup.invoices?.[0]?.note) {
        setSavedNote(invoiceGroup.invoices[0].note)
      } else {
        setSavedNote('')
      }
    }
  }, [invoiceGroup])

  const notePopoverContent = (
    <Vertical gap={16}>
      <Input.TextArea
        value={customerNote}
        onChange={(e) => setCustomerNote(e.target.value)}
        placeholder='Enter customer note...'
        rows={3}
        maxLength={180}
        showCount
      />
      <GraviButton
        buttonText='Save Note'
        onClick={handleSaveNote}
        disabled={!customerNote.trim()}
        style={{ width: '100%' }}
        color='primary'
      />
    </Vertical>
  )

  return (
    <>
      <Vertical className='p-3' bg='default' flex='0' style={{ overflow: 'visible' }} gap={8}>
        <Vertical flex='0' style={{ overflow: 'visible' }} className='py-2' gap={8}>
          <Vertical style={{ overflow: 'visible' }} verticalCenter>
            <Texto category='h4' weight='600'>
              {invoiceGroup.group_number}
            </Texto>
            <Texto category='p2' weight='600' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MaterialIcon icon='user_attributes' size={16} />
              {invoiceGroup.counterparty_name}
            </Texto>
          </Vertical>
          <Horizontal style={{ overflow: 'visible' }} className='pb-2' verticalCenter>
            <Texto style={{ flex: 1 }} appearance='light' category='p2'>
              Invoice Period
            </Texto>
            <Texto appearance='light' category='p2'>
              {dayjs(invoiceGroup.start_date).format('MM/DD/YYYY')} -{' '}
              {dayjs(invoiceGroup.end_date).format('MM/DD/YYYY')}
            </Texto>
          </Horizontal>
          <Horizontal style={{ overflow: 'visible' }} className='pb-2' verticalCenter>
            <Texto style={{ flex: 1 }} appearance='light' category='p2'>
              Due Date
            </Texto>
            <Texto appearance='light' category='p2'>
              {dayjs(invoiceGroup.due_date).format('MM/DD/YYYY')}
            </Texto>
          </Horizontal>

          {/* Status Section - More Prominent */}
          <Vertical
            flex='0'
            style={{ overflow: 'visible' }}
            className='py-2 px-3 my-2'
            border='all'
            borderRadius='6px'
            bg={invoiceStatusConfig[invoiceGroup.status as InvoiceStatus]?.bgColor || 'default'}
          >
            <Horizontal verticalCenter gap={12}>
              <MaterialIcon
                icon={invoiceStatusConfig[invoiceGroup.status as InvoiceStatus]?.icon}
                size={24}
                style={{
                  color: invoiceStatusConfig[invoiceGroup.status as InvoiceStatus]?.iconColor || 'var(--text-primary)',
                }}
              />
              <Vertical style={{ overflow: 'visible' }} gap={2}>
                <Texto style={{ textTransform: 'capitalize' }} category='p2' weight='600'>
                  {invoiceStatusConfig[invoiceGroup.status as InvoiceStatus]?.title || invoiceGroup.status}
                </Texto>

                {invoiceStatusConfig[invoiceGroup.status as InvoiceStatus]?.renderInfo(invoiceGroup)}
              </Vertical>
            </Horizontal>
          </Vertical>

          {savedNote && (
            <Vertical style={{ overflow: 'visible' }} flex='0' gap={4}>
              <Horizontal
                style={{ overflow: 'visible' }}
                verticalCenter
                justifyContent='space-between'
                className='border-top pt-2'
              >
                <Texto category='p2' appearance='light'>
                  Customer Note:
                </Texto>
                {invoiceGroup.status !== 'sent' && !scopes.is_carrier && (
                  <Horizontal gap={8}>
                    <Popover
                      content={notePopoverContent}
                      title='Edit Customer Note'
                      trigger='click'
                      open={isEditPopoverVisible}
                      onOpenChange={setIsEditPopoverVisible}
                    >
                      <MaterialIcon
                        icon='edit'
                        size={16}
                        style={{
                          color: 'var(--text-light)',
                          cursor: 'pointer',
                        }}
                        onClick={handleEditNote}
                      />
                    </Popover>
                    <MaterialIcon
                      icon='delete'
                      size={16}
                      style={{ color: 'var(--text-light)', cursor: 'pointer' }}
                      onClick={handleRemoveNote}
                    />
                  </Horizontal>
                )}
              </Horizontal>
              <Texto>{savedNote}</Texto>
            </Vertical>
          )}
        </Vertical>

        {!scopes?.is_carrier && (
          <Vertical gap={8} className='mt-2'>
            <Vertical className='pb-3' style={{ overflow: 'visible' }} gap={8}>
              <Texto appearance='light'>Actions</Texto>
              <Vertical gap={8}>
                {invoiceGroup.status === 'cr_approval' && scopes?.freight?.approve && (
                  <ApproveCreditButton invoiceGroup={invoiceGroup} />
                )}
                {invoiceGroup.status === 'cr_approval' && scopes?.freight?.approve && (
                  <RejectCreditButton invoiceGroup={invoiceGroup} />
                )}
                {!savedNote && invoiceGroup.status !== 'sent' && (
                  <AddCustomerNoteButton
                    isNotePopoverVisible={isNotePopoverVisible}
                    setIsNotePopoverVisible={setIsNotePopoverVisible}
                    notePopoverContent={notePopoverContent}
                  />
                )}
                {(invoiceGroup.status === 'sent' || invoiceGroup.status === 'failure') && (
                  <ResendButton invoiceGroup={invoiceGroup} />
                )}
                {canManageHold && (
                  <Horizontal gap={8} style={{ width: '100%' }}>
                    <div className='flex-1'>
                      <ManageHoldButton invoiceGroup={invoiceGroup} />
                    </div>
                    {canSend && (
                      <div className='flex-1'>
                        <SendButton invoiceGroup={invoiceGroup} />
                      </div>
                    )}
                  </Horizontal>
                )}
                {invoiceGroup.status === 'sent' && (
                  <CreditRebillButton onClick={triggerRebillMode} loading={isTriggeringCR} />
                )}
              </Vertical>
              {showDocuments && <InvoiceDocumentsSection invoiceGroup={invoiceGroup} />}
            </Vertical>
          </Vertical>
        )}
      </Vertical>
      <Vertical bg='default'>
        <Vertical
          bg='default'
          border='all'
          borderRadius='6px'
          style={{ height: '100%', maxHeight: '100%' }}
          className=' m-3'
        >
          <Horizontal border='bottom' verticalCenter gap={8} className='px-3 py-2'>
            <MaterialIcon icon='comment' size={12} />
            <Texto>Comments</Texto>
          </Horizontal>
          <InvoiceCommentsContainer invoiceGroupId={invoiceGroup.id} />
        </Vertical>
      </Vertical>
    </>
  )
}
